import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import StructuredData from '@/components/base/StructuredData';
import ComicCard from '@/components/feature/ComicCard';
import { allComics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = allComics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500">漫画不存在</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
      </>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const related = allComics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const formatViews = (v: number): string => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <>
      <SEOMeta
        title={`${comic.title} - 樱花动漫91漫画全集下拉式阅读`}
        description={`${comic.title}，${comic.author}作品。${comic.description.slice(0, 120)}。樱花动漫91提供${comic.title}漫画全集下拉式阅读，免费在线看。`}
        keywords={`${comic.title},${comic.author},${comic.tags.join(',')},漫画全集,下拉式漫画,樱花动漫91`}
        canonicalUrl={`https://yinghuadongman91.com/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        articlePublishedTime={comic.chapters[0]?.date}
        articleModifiedTime={comic.updateDate}
      />
      <StructuredData
        type="Article"
        articleData={{
          headline: comic.title,
          description: comic.description,
          image: comic.cover,
          datePublished: comic.chapters[0]?.date || '',
          dateModified: comic.updateDate,
          authorName: comic.author,
        }}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${category?.name || ''}漫画`, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <div className="bg-white rounded-xl p-6 md:p-8 mb-8">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-[200px] flex-shrink-0 mx-auto md:mx-0">
                  <div className="rounded-lg overflow-hidden aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 樱花动漫91漫画全集`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                      {comic.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-accent-500"></i>
                      <strong className="text-accent-500">{comic.rating}</strong> 分
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                      {formatViews(comic.views)} 阅读
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        comic.status === '连载中'
                          ? 'bg-primary-50 text-primary-600'
                          : 'bg-secondary-100 text-secondary-700'
                      }`}
                    >
                      {comic.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                    {comic.description}
                  </p>

                  <Link
                    to={`/comic/${comic.id}/read/1`}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                  >
                    <i className="ri-book-open-line w-4 h-4 flex items-center justify-center"></i>
                    开始阅读
                  </Link>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 mb-8">
              <h2 className="font-heading text-xl text-foreground-950 mb-5">
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  共 {comic.chapters.length} 话
                </span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {comic.chapters.map((ch) => (
                  <Link
                    key={ch.num}
                    to={`/comic/${comic.id}/read/${ch.num}`}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors duration-200 group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors duration-200">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.date}</span>
                  </Link>
                ))}
              </div>
            </div>

            {related.length > 0 && (
              <div className="pb-14">
                <h2 className="font-heading text-xl text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
                  {related.map((r) => (
                    <ComicCard key={r.id} {...r} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}